import { createApiClient, apiClient } from '../../../../../api/apiClient';

// ─── Types ────────────────────────────────────────────────────────────────────

/**
 * Basic dropdown option with label and value.
 */
export interface DropdownOption {
  label: string;
  value: string;
}

/**
 * Location option with additional location ID.
 * Returned for shipping and receiving locations.
 */
export interface LocationOption extends DropdownOption {
  locId: string;
}

/**
 * Request body for ASN location dropdown filters.
 * All fields are optional; null/empty = return all available options.
 */
export interface AsnLocationFilterRequest {
  shippingRegions?: string[];
  shippingMarkets?: string[];
  shippingLocationIds?: string[];
  receivingLocationIds?: string[];
}

/**
 * Response body with all four dropdown lists.
 * All fields are guaranteed to be non-null arrays (may be empty).
 * `lastRefreshedAt` is an ISO-8601 UTC timestamp of when the backend data
 * was last refreshed — optional/nullable since older backends may omit it.
 */
export interface AsnLocationFilterResponse {
  shippingRegions: DropdownOption[];
  shippingMarkets: DropdownOption[];
  shippingLocations: LocationOption[];
  receivingLocations: LocationOption[];
  lastRefreshedAt?: string | null;
}

// ─── Service Function ─────────────────────────────────────────────────────────

/**
 * Fetch ASN location dropdown filters with optional cascading filters.
 *
 * POST /asn/filters/dropdowns
 *
 * Cascading Rules:
 * - shippingRegions: No filters, returns all distinct regions
 * - shippingMarkets: Filtered by shippingRegions (or all if empty)
 * - shippingLocations: Filtered by shippingRegions + shippingMarkets (or all if empty)
 * - receivingLocations: Filtered by shippingRegions + shippingMarkets + shippingLocationIds
 *                      (ignores receivingLocationIds in request)
 *
 * Key behaviors:
 * - Null/empty request: Initialize with empty object
 * - Empty arrays: Use placeholder "__PLACEHOLDER__" in WHERE IN clauses
 * - Null SAP IDs: Excluded from results
 * - Results: Sorted alphabetically by label, duplicates removed
 *
 * @param request - Optional filter criteria
 * @returns Typed AsnLocationFilterResponse with all dropdown lists
 */
export async function fetchAsnLocationDropdownFilters(
  request?: AsnLocationFilterRequest
): Promise<AsnLocationFilterResponse> {
  const api = createApiClient();

  // Normalize request: null body → empty object
  const normalizedRequest: AsnLocationFilterRequest = request || {};

  // Sanitize arrays: trim whitespace, remove nulls/blanks, deduplicate
  const sanitizeArray = (arr?: string[]): string[] => {
    if (!arr) return [];
    const cleaned = arr
      .map((item) => (typeof item === 'string' ? item.trim() : ''))
      .filter((item) => item !== '');
    return Array.from(new Set(cleaned));
  };

  const sanitizedRequest: AsnLocationFilterRequest = {
    shippingRegions: sanitizeArray(normalizedRequest.shippingRegions),
    shippingMarkets: sanitizeArray(normalizedRequest.shippingMarkets),
    shippingLocationIds: sanitizeArray(normalizedRequest.shippingLocationIds),
    receivingLocationIds: sanitizeArray(normalizedRequest.receivingLocationIds),
  };

  const endpoint = '/asn/filters/dropdowns';

  try {
    const response = await apiClient(api, endpoint, 'POST', sanitizedRequest);
    return response as AsnLocationFilterResponse;
  } catch (error) {
    console.error('Error fetching ASN location dropdown filters:', error);
    throw error;
  }
}
